import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";

import DirectPayment from "components/DirectPayment/DirectPayment";
import LoanPayment from "components/LoanPayment/LoanPayment";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import {
  DetailDisplay,
  Dialog,
  ErrorMessage,
  SingleSelect,
  Text,
} from "widgets";
import PaymentDetails from "components/PaymentDetails/PaymentDetails";
import { useSelectedOrderDetails } from "store/backend/assets";
import DisplayOrder from "components/DisplayOrder/DisplayOrder";
import {
  INR_SYMBOL,
  SUCCESSFUL_REFUND_STATUSES,
  PAYMENT_STATUSES,
  SUCCESSFUL_PAYMENT_STATUSES,
  FULL_PAYMENT,
  PART_PAYMENT,
  FULL_PAYMENT_FLOW_TYPE_ID,
  LEARN_VERTICAL_ID,
  LOAN_PAYMENT_METHOD,
  PART_PAYMENT_FLOW_TYPE_ID,
  KH_VERTICAL_ID,
  PAYMENT_PLANS,
  PAYMENT_PLAN_OPTIONS,
} from "constants/config";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import AddNote from "components/AddNote/AddNote";
import PartPayment from "components/PartPayment/PartPayment";
import { reduceToFixedDecimals } from "utils/amountCalculation";
import FullPageLoader from "Shared/FullPageLoader/FullPageLoader";

import styles from "./PaymentPreference.module.scss";

const handleDialogClose = (setOpenDialogContent) => () => {
  setOpenDialogContent(null);
};

const handleOnClickAddNote = (setOpenDialogContent) => {
  setOpenDialogContent(true);
};
const checkIfLearnEligibleForPartPayment = (
  totalAmount,
  amountPaid,
  paymentDetails
) => {
  const paymentUptoTwentyFivePercent = totalAmount * 0.25;
  const isLoanCancelled = paymentDetails.some(
    (payment) =>
      payment.method?.id === LOAN_PAYMENT_METHOD &&
      payment.status.id === PAYMENT_STATUSES.REJECTED_BY_INFINITY_LEARN
  );
  if (amountPaid >= paymentUptoTwentyFivePercent && isLoanCancelled) {
    return true;
  }
  return false;
};

const checkIfLoanCreated = (paymentDetails = []) => {
  if (!Array.isArray(paymentDetails)) return false;

  return paymentDetails.some(
    (payment) =>
      payment?.method?.id === LOAN_PAYMENT_METHOD &&
      payment?.status?.id === PAYMENT_STATUSES.CREATED
  );
};

const showSelectPaymentPlanDropdown = (isPartPaymentScheduleConfirmed) => {
  if (isPartPaymentScheduleConfirmed) {
    return false;
  }
  return true;
};

const getDefaultPaymentPlan = (selectedFlowType) =>
  selectedFlowType
    ? selectedFlowType === FULL_PAYMENT_FLOW_TYPE_ID
      ? PAYMENT_PLANS.FULL_PAYMENT
      : selectedFlowType === PART_PAYMENT_FLOW_TYPE_ID
      ? PAYMENT_PLANS.PART_PAYMENT
      : PAYMENT_PLANS.LOAN
    : null;

const PaymentPreference = () => {
  const [searchParams] = useSearchParams();
  const orderId = searchParams.get("order_id");
  const salesPersonId = localStorage.getItem("sales_person_id");
  const [openDialogContent, setOpenDialogContent] = useState(null);

  const {
    isPending: isSelectedOrderDetailsLoading,
    data: selectedOrderDetails,
    isError: isErrorOnSelectedOrderDetails,
    error: errorMessageOnSelectedOrderDetails,
  } = useSelectedOrderDetails(orderId);

  const isPartPaymentScheduleConfirmed =
    selectedOrderDetails?.part_payment_details?.length > 0 || false;

  const selectedOrderVerticalId =
    selectedOrderDetails && selectedOrderDetails.business_unit_id;

  const isAnyAmountPaid =
    selectedOrderDetails && selectedOrderDetails.amount_paid > 0;

  const selectedFlowType =
    selectedOrderDetails && selectedOrderDetails.payment_flow_type?.id;
  const [paymentPlan, setPaymentPlan] = useState(
    getDefaultPaymentPlan(selectedFlowType)
  );

  const headAmount =
    String(selectedOrderVerticalId) === String(KH_VERTICAL_ID)
      ? (() => {
          const paymentHeads =
            selectedOrderDetails?.packages?.[0]?.subscriptions?.[0]
              ?.payment_heads;

          if (!Array.isArray(paymentHeads) || paymentHeads.length === 0) {
            return undefined;
          }

          const firstHead = paymentHeads[0];
          return Number(firstHead.amount_paid) < Number(firstHead.amount)
            ? firstHead.amount
            : undefined;
        })()
      : undefined;

  useEffect(() => {
    if (isPartPaymentScheduleConfirmed) {
      setPaymentPlan(PART_PAYMENT);
    } else if (selectedOrderVerticalId === LEARN_VERTICAL_ID) {
      setPaymentPlan(FULL_PAYMENT);
    } else if (selectedFlowType === FULL_PAYMENT_FLOW_TYPE_ID) {
      setPaymentPlan(FULL_PAYMENT);
    } else if (selectedFlowType === PART_PAYMENT_FLOW_TYPE_ID) {
      setPaymentPlan(PART_PAYMENT);
    }
  }, [isPartPaymentScheduleConfirmed, selectedOrderDetails]);

  if (isSelectedOrderDetailsLoading) {
    return <FullPageLoader />;
  }

  if (isErrorOnSelectedOrderDetails) {
    return <div>{errorMessageOnSelectedOrderDetails.message}</div>;
  }

  if (!selectedOrderDetails) {
    return (
      <ErrorMessage>
        Order Details not found for Order Id {orderId}
      </ErrorMessage>
    );
  }

  const totalAmount = reduceToFixedDecimals(
    selectedOrderDetails.inr_conversion_amount
  );

  const totalSuccessfulPaymentAmount = reduceToFixedDecimals(
    selectedOrderDetails.payment_details
      .filter((payment) =>
        SUCCESSFUL_PAYMENT_STATUSES.includes(payment.status.id)
      )
      .reduce((sum, payment) => sum + payment.amount, 0)
  );

  const totalSuccessfulRefundedAmount = reduceToFixedDecimals(
    selectedOrderDetails.refund_details
      .filter((refund) => SUCCESSFUL_REFUND_STATUSES.includes(refund.status.id))
      .reduce((sum, refund) => sum + refund.refunded_amount, 0)
  );

  const pendingAmount = Math.max(
    0,
    reduceToFixedDecimals(
      totalAmount - totalSuccessfulPaymentAmount + totalSuccessfulRefundedAmount
    )
  );

  const paymentUnderProcessing = reduceToFixedDecimals(
    selectedOrderDetails.payment_details.length > 0
      ? selectedOrderDetails.payment_details
          .filter((payment) => payment.status.id === PAYMENT_STATUSES.CREATED)
          .reduce((sum, payment) => sum + payment.amount, 0)
      : 0
  );

  const amount_paid = reduceToFixedDecimals(
    totalSuccessfulPaymentAmount - totalSuccessfulRefundedAmount
  );

  const excess_amount_paid = Math.min(
    0,
    reduceToFixedDecimals(
      totalAmount - totalSuccessfulPaymentAmount + totalSuccessfulRefundedAmount
    )
  );

  const paymentDetails =
    (selectedOrderDetails && selectedOrderDetails.payment_details) || [];

  const isLoanCreated = checkIfLoanCreated(paymentDetails);


  const isLearnEligibleForPartPayment = checkIfLearnEligibleForPartPayment(
    totalAmount,
    amount_paid,
    paymentDetails
  );

  const filteredPaymentOptions = PAYMENT_PLAN_OPTIONS.filter((option) => {
    if (
      option.value === PAYMENT_PLANS.PART_PAYMENT &&
      selectedOrderVerticalId === LEARN_VERTICAL_ID
    ) {
      return false;
    }
    if (
      option.value === PAYMENT_PLANS.PART_PAYMENT &&
      selectedOrderVerticalId === KH_VERTICAL_ID &&
      isLoanCreated
    )
      return false;
    return true;
  });

  const isFullAmountPaid = totalAmount <= amount_paid;

  return (
    <div className={styles.mainContainer}>
      <div className={styles.contentWrapper}>
        <div className={styles.paymentArea}>
          {!isFullAmountPaid ? (
            <>
              {showSelectPaymentPlanDropdown(
                isPartPaymentScheduleConfirmed
              ) && (
                <div className={styles.field}>
                  <Text>Select Payment Plan</Text>
                  <SingleSelect
                    name="paymentPlan"
                    value={paymentPlan}
                    handleChange={(event) => setPaymentPlan(event.target.value)}
                    menuItems={filteredPaymentOptions}
                  />
                </div>
              )}
              {paymentPlan === PAYMENT_PLANS.FULL_PAYMENT && (
                <DirectPayment
                  totalAmount={totalAmount}
                  payableAmount={pendingAmount}
                  orderId={orderId}
                  salesPersonId={salesPersonId}
                  headAmount={headAmount}
                  selectedOrderVerticalId={selectedOrderVerticalId}
                />
              )}
              {(paymentPlan === PAYMENT_PLANS.PART_PAYMENT ||
                isPartPaymentScheduleConfirmed) && (
                <PartPayment
                  selectedOrderId={orderId}
                  isPartPaymentScheduleConfirmed={
                    isPartPaymentScheduleConfirmed
                  }
                  selectedPartPaymentDetails={
                    selectedOrderDetails.part_payment_details
                  }
                  isAnyAmountPaid={isAnyAmountPaid}
                  salesPersonId={salesPersonId}
                  selectedOrderVerticalId={selectedOrderVerticalId}
                  selectedFlowType={selectedFlowType}
                />
              )}
              {paymentPlan === PAYMENT_PLANS.LOAN && (
                <LoanPayment
                  selected_order_id={orderId}
                  pendingAmount={pendingAmount}
                  totalAmount={totalAmount}
                  selectedOrderVerticalId={selectedOrderVerticalId}
                  salesPersonId={salesPersonId}
                />
              )}
            </>
          ) : (
            <div className={styles.successfulPaymentMessage}>
              <CheckCircleIcon className={styles.successIcon} />
              <div className={styles.successFullPaymentText}>
                The total amount for this order has been successfully paid.
              </div>
            </div>
          )}
        </div>
        <div className={styles.orderDetails}>
          <div className={styles.amountDetails}>
            <DetailDisplay
              title="Total Order Amount"
              value={INR_SYMBOL + reduceToFixedDecimals(totalAmount)}
            />
            <DetailDisplay
              title="Total Amount Paid"
              value={INR_SYMBOL + reduceToFixedDecimals(amount_paid)}
            />
            <DetailDisplay
              title="Total Pending Amount"
              value={
                INR_SYMBOL + Math.abs(reduceToFixedDecimals(pendingAmount))
              }
            />
            <DetailDisplay
              title="Amount Under Processing"
              value={
                INR_SYMBOL +
                Math.abs(reduceToFixedDecimals(paymentUnderProcessing))
              }
            />
            <DetailDisplay
              title="Excess Amount Paid"
              value={
                INR_SYMBOL + Math.abs(reduceToFixedDecimals(excess_amount_paid))
              }
            />
          </div>
          <DisplayOrder
            selectedPackageSubscriptions={selectedOrderDetails.packages}
          />
          <div
            className={styles.addNote}
            onClick={() => handleOnClickAddNote(setOpenDialogContent)}
          >
            Add a Note?
            <AddCircleIcon />
          </div>
          {selectedOrderDetails && (
            <PaymentDetails
              paymentDetails={selectedOrderDetails.payment_details}
            />
          )}
        </div>
      </div>
      <Dialog open={openDialogContent !== null} width="30rem" height="21rem">
        <AddNote
          onClose={handleDialogClose(setOpenDialogContent)}
          orderId={orderId}
        />
      </Dialog>
    </div>
  );
};

export default PaymentPreference;
